import React, { forwardRef } from 'react';
import { ExclamationCircleIcon, CheckCircleIcon } from '@heroicons/react/24/outline';

interface FormTextareaProps extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  label?: string;
  error?: string;
  success?: string;
  helperText?: string;
  required?: boolean;
  loading?: boolean;
  maxLength?: number;
  showCharCount?: boolean;
  size?: 'sm' | 'md' | 'lg';
  variant?: 'default' | 'filled' | 'outlined';
  autoResize?: boolean;
}

const FormTextarea = forwardRef<HTMLTextAreaElement, FormTextareaProps>(({
  label,
  error,
  success,
  helperText,
  required,
  loading,
  maxLength,
  showCharCount,
  size = 'md',
  variant = 'default',
  autoResize,
  className = '',
  value,
  onChange,
  ...props
}, ref) => {
  const [focused, setFocused] = React.useState(false);
  const [charCount, setCharCount] = React.useState(0);
  const textareaRef = React.useRef<HTMLTextAreaElement | null>(null);

  React.useEffect(() => {
    if (typeof value === 'string') {
      setCharCount(value.length);
    }
  }, [value]);

  React.useEffect(() => {
    if (autoResize && textareaRef.current) {
      const textarea = textareaRef.current;
      textarea.style.height = 'auto';
      textarea.style.height = `${textarea.scrollHeight}px`;
    }
  }, [value, autoResize]);

  const sizeClasses = {
    sm: 'px-3 py-2 text-sm min-h-[80px]',
    md: 'px-3 py-2.5 text-sm min-h-[100px]',
    lg: 'px-4 py-3 text-base min-h-[120px]',
  };

  const variantClasses = {
    default: 'border border-gray-300 bg-white',
    filled: 'border-0 bg-gray-100',
    outlined: 'border-2 border-gray-300 bg-transparent',
  };

  const getStateClasses = () => {
    if (error) {
      return 'border-red-500 ring-red-500 focus:border-red-500 focus:ring-red-500';
    }
    if (success) {
      return 'border-green-500 ring-green-500 focus:border-green-500 focus:ring-green-500';
    }
    if (focused) {
      return 'border-[#008B8B] ring-[#008B8B] focus:border-[#008B8B] focus:ring-[#008B8B]';
    }
    return 'focus:border-[#008B8B] focus:ring-[#008B8B]';
  };

  const textareaClasses = [
    'w-full rounded-lg transition-colors duration-200 resize-none',
    'focus:outline-none focus:ring-2 focus:ring-opacity-50',
    'disabled:bg-gray-100 disabled:text-gray-500 disabled:cursor-not-allowed',
    sizeClasses[size],
    variantClasses[variant],
    getStateClasses(),
    className,
  ].filter(Boolean).join(' ');

  const handleFocus = (e: React.FocusEvent<HTMLTextAreaElement>) => {
    setFocused(true);
    props.onFocus?.(e);
  };

  const handleBlur = (e: React.FocusEvent<HTMLTextAreaElement>) => {
    setFocused(false);
    props.onBlur?.(e);
  };

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newValue = e.target.value;
    
    // Check max length
    if (maxLength && newValue.length > maxLength) {
      return;
    }

    setCharCount(newValue.length);
    onChange?.(e);
  };

  const setRef = (element: HTMLTextAreaElement | null) => {
    textareaRef.current = element;
    if (typeof ref === 'function') {
      ref(element);
    } else if (ref) {
      ref.current = element;
    }
  };

  return (
    <div className="w-full">
      {/* Label */}
      {label && (
        <label className="block text-sm font-medium text-gray-700 mb-2">
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </label>
      )}

      {/* Textarea Container */}
      <div className="relative">
        {/* Textarea */}
        <textarea
          ref={setRef}
          className={textareaClasses}
          value={value}
          onChange={handleChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          disabled={loading}
          maxLength={maxLength}
          style={autoResize ? { resize: 'none', overflow: 'hidden' } : undefined}
          {...props}
        />

        {/* Loading Overlay */}
        {loading && (
          <div className="absolute inset-0 bg-white bg-opacity-50 flex items-center justify-center rounded-lg">
            <div className="animate-spin rounded-full h-6 w-6 border-2 border-gray-300 border-t-[#008B8B]"></div>
          </div>
        )}

        {/* Status Icons */}
        {(error || success) && !loading && (
          <div className="absolute top-3 right-3">
            {error && <ExclamationCircleIcon className="h-5 w-5 text-red-500" />}
            {success && !error && <CheckCircleIcon className="h-5 w-5 text-green-500" />}
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="mt-1 flex justify-between items-start">
        {/* Helper Text / Error / Success Message */}
        <div className="flex-1">
          {error && (
            <div className="text-red-600 text-sm flex items-center">
              <ExclamationCircleIcon className="h-4 w-4 mr-1 flex-shrink-0" />
              {typeof error === 'string' ? error : (error as any)?.message || 'An error occurred'}
            </div>
          )}
          {success && !error && (
            <div className="text-green-600 text-sm flex items-center">
              <CheckCircleIcon className="h-4 w-4 mr-1 flex-shrink-0" />
              {success}
            </div>
          )}
          {helperText && !error && !success && (
            <div className="text-gray-500 text-sm">{helperText}</div>
          )}
        </div>

        {/* Character Count */}
        {(showCharCount || maxLength) && (
          <div className="text-xs text-gray-500 ml-2 flex-shrink-0">
            {charCount}
            {maxLength && `/${maxLength}`}
          </div>
        )}
      </div>
    </div>
  );
});

FormTextarea.displayName = 'FormTextarea';

export default FormTextarea;